import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@application/config';
import { DateTime } from 'luxon';
import { map, Observable } from 'rxjs';
import {
  TAuditEventFilters,
  TAuditEventPage,
  TAuditEventView,
} from '../entities/audit-event.model';

/**
 * Ajustes › Auditoria: the OData set `AuditEvents` of the personservice
 * (`AuditoriaVisualizar`, 307; `$top` ≤ 1000). Read only.
 *
 * Literals, as this backend reads them:
 * - Guids bare (`UserId eq 6f1c…`).
 * - Enums by member name, quoted (`Event eq 'CustomerInactivated'`): OData v4
 *   names them that way in the payload too. Not yet checked against the live
 *   API.
 * - `OccurredOn` as Brasília wall time with a decorative `Z` (see `toFilter`).
 * - Several actions as an `or` chain — never `in`, whose URI grows past 414.
 */
@Injectable({ providedIn: 'root' })
export class AuditEventService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.odata_person_service;

  /** The zone of the `OccurredOn` column (`timeline.addedon`), whatever the account's. */
  private readonly columnZone = 'America/Sao_Paulo';

  /** One page, newest first, with the total of the filtered query. */
  public getPage(
    filters: TAuditEventFilters,
    skip: number,
    top: number
  ): Observable<TAuditEventPage> {
    const filter = this.toFilter(filters);
    const params: Record<string, string | number | boolean> = {
      $count: true,
      $orderby: 'OccurredOn desc,AuditEventId desc',
      $skip: skip,
      $top: top,
    };
    if (filter) {
      params['$filter'] = filter;
    }

    return this.http
      .get<{ '@odata.count'?: number; value: TAuditEventView[] }>(
        `${this.baseUrl}/AuditEvents`,
        { params, withCredentials: true }
      )
      .pipe(
        map((response) => ({
          Total: response['@odata.count'] ?? response.value.length,
          Items: response.value,
        }))
      );
  }

  private toFilter(filters: TAuditEventFilters): string {
    const clauses: string[] = [];

    // The days are the account's (a bare `fromISO` reads them in its zone,
    // set by `LUXON_ZONE_INITIALIZER`); the column holds Brasília wall time
    // with no offset. So each bound moves to Brasília and goes out with a
    // decorative `Z`, the house form for wall-time columns: a literal with an
    // offset is converted by the server and lands 3 hours off on one running in
    // UTC. `@shared/utils` is not imported here: it closes a cycle back into
    // this lib through the dx-data-grid.
    if (filters.FromDay) {
      const from = this.columnLiteral(DateTime.fromISO(filters.FromDay).startOf('day'));
      if (from) {
        clauses.push(`OccurredOn ge ${from}`);
      }
    }
    if (filters.ToDay) {
      const until = this.columnLiteral(
        DateTime.fromISO(filters.ToDay).startOf('day').plus({ days: 1 })
      );
      if (until) {
        clauses.push(`OccurredOn lt ${until}`);
      }
    }
    if (filters.UserId) {
      clauses.push(`UserId eq ${filters.UserId}`);
    }
    if (filters.PersonId) {
      clauses.push(`PersonId eq ${filters.PersonId}`);
    }
    if (filters.Origin) {
      clauses.push(`Origin eq '${filters.Origin}'`);
    }
    const events = filters.Events ?? [];
    if (events.length === 1) {
      clauses.push(`Event eq '${events[0]}'`);
    } else if (events.length > 1) {
      clauses.push(
        `(${events.map((event) => `Event eq '${event}'`).join(' or ')})`
      );
    }

    return clauses.join(' and ');
  }

  /** An instant as the column's wall time, `yyyy-MM-ddTHH:mm:ssZ`; `null` when invalid. */
  private columnLiteral(instant: DateTime): string | null {
    return instant.isValid
      ? instant.setZone(this.columnZone).toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'")
      : null;
  }
}
